import { ISimpleList } from '@nocode/types';
import { findLastIndex, get, isEmpty, isNil } from 'lodash';
import queryString from 'query-string';
import React, { useMemo } from 'react';
import { FlatList, Platform, Text, TouchableOpacity, View } from 'react-native';
import { List } from 'react-native-paper';
import LeftSection from '../Common/SectionLeftRight';
import EmptyList from '../EmptyList';
import { checkFont, getListItems } from '../func';
import { getActions, getItemListClick, getValueBinding } from '../shared';
import Loading from '../Loading';
import { processStyles } from '../util.style';
import createStyles from './style';

const SimpleList = (attrs: ISimpleList) => {
  const {
    attributes: {
      listHeader,
      leftSection,
      rightSection,
      dividerColor,
      dividerType,
      scrollActive,
      iconColor,
      opacity,
      backgroundColor,
      borderColor,
      borderWidth,
      borderRadius,
      fontFamily,
      fontSize,
      color,
      rowCount,
      items,
    },
    dataBinding,
    isWeb,
    page,
    totalPage,
    onLoadMore,
    isLoadMore,
    onPress,
    initializeList,
  } = attrs;

  // Process styles similar to HorizontalCardList
  const { finalStyle, componentProps } = processStyles({
    style: attrs.style,
    ...attrs,
  });
  const { subTitle, subTitle2, title } = componentProps;
  const handlePress = (
    id: 'onPress' | 'rightSection',
    item: Record<string, any>
  ) => {
    const options = {
      itemListClick: getItemListClick(item.record),
    };

    onPress && onPress(getActions(attrs, id), options);
  };

  const isShowFooter = totalPage > page;

  const getInitValues = () => {
    let len = rowCount || 3;

    if (Platform.OS !== 'web') {
      // App
      return dataBinding;
    } else {
      // web
      const search = queryString.parse(window?.location?.search);
      const target = search?.target;

      if (target) {
        // preview
        return dataBinding;
      } else {
        // client
        return getListItems(items, len);
      }
    }
  };

  const listData = useMemo(() => getInitValues(), [dataBinding]);

  const styles = createStyles();

  const isFixedHeight: any = scrollActive
    ? { contentContainerStyle: { flexGrow: 0, height: 'auto', margin: 0 } }
    : {};

  const tableId = get(attrs, 'attributes.items.source.tableId');

  const search = !isEmpty(window)
    ? queryString.parse(window?.location?.search)
    : {};

  const target = search?.target;

  const emptyList = (target && !tableId) || (!tableId && Platform.OS !== 'web');

  if (emptyList)
    return (
      <View
        style={{
          display: 'flex',
          flexDirection: 'row',
          flexWrap: 'wrap',
          zIndex: attrs.zIndex,
        }}
      >
        <EmptyList attributes={attrs} />
      </View>
    );

  if (initializeList) {
    return <Loading />;
  }

  const ListFooterComponent = () => {
    return (
      <TouchableOpacity
        onPress={() => {
          if (isShowFooter) {
            onLoadMore();
          }
        }}
      >
        {isLoadMore ? (
          <Loading />
        ) : (
          <Text
            style={{
              fontSize: 14,
              textDecorationLine: 'underline',
              fontWeight: 'bold',
              textAlign: 'center',
            }}
          >
            もっとロード
          </Text>
        )}
      </TouchableOpacity>
    );
  };

  const renderItemList = ({ item, index }: any) => {
    const bindingValue = getValueBinding('', item, attrs);

    const titleTextStyle = {
      ...(title?.styles?.text ?? {}),
      fontSize: title?.styles?.text?.fontSize || fontSize,
      fontFamily: checkFont(title?.styles?.text?.fontFamily || fontFamily),
      color: title?.styles?.text?.color || color,
      fontWeight: title?.styles?.text?.fontWeight?.toString(),
      backgroundColor: 'transparent',
    };

    const subtitleTextStyle = {
      fontSize: subTitle?.styles?.text?.fontSize || fontSize,
      fontFamily: checkFont(subTitle?.styles?.text?.fontFamily || fontFamily),
      color: subTitle?.styles?.text?.color || color,
      fontWeight: subTitle?.styles?.text?.fontWeight?.toString(),
    };

    const subtitle2TextStyle = {
      fontSize: subTitle2?.styles?.text?.fontSize || fontSize,
      fontFamily: checkFont(subTitle2?.styles?.text?.fontFamily || fontFamily),
      color: subTitle2?.styles?.text?.color || color,
      fontWeight: subTitle2?.styles?.text?.fontWeight?.toString(),
    };

    const listItemStyle = {
      borderTopWidth:
        dividerType && dividerType === 'none'
          ? 0
          : index === 0 && dividerType && dividerType === 'line'
          ? 0
          : dividerType && dividerType === 'shadow'
          ? 1
          : 0,
      borderBottomWidth:
        dividerType && dividerType === 'none'
          ? 0
          : index !== findLastIndex(listData)
          ? 1
          : dividerType && dividerType === 'shadow'
          ? 1
          : 0,
      borderColor: finalStyle?.borderColor || dividerColor || '#e2e1e1',
      justifyContent: 'center',
      ...(dividerType &&
        dividerType === 'shadow' && {
          borderRadius: finalStyle?.borderRadius || 20,
        }),
    };

    return (
      <List.Item
        key={index}
        title={get(bindingValue, 'title.text')}
        titleNumberOfLines={title && title.titleLineNum > 1 ? 500 : 1}
        onPress={() => {
          handlePress('onPress', {
            record: item,
            indexRecord: index,
          });
        }}
        description={() => (
          <View>
            {subTitle.enabled && (
              <Text
                numberOfLines={
                  subTitle && subTitle.subtitleLineNum > 1 ? 500 : 1
                }
                style={subtitleTextStyle}
              >
                {get(bindingValue, 'subTitle.text')}
              </Text>
            )}
            {subTitle2.enabled && (
              <Text
                style={subtitle2TextStyle}
                numberOfLines={
                  subTitle2 && subTitle2.subtitle2LineNum > 1 ? 500 : 1
                }
              >
                {get(bindingValue, 'subTitle2.text')}
              </Text>
            )}
          </View>
        )}
        left={() =>
          leftSection?.enabled ? (
            <LeftSection
              attributes={{
                ...(leftSection || {}),
                iconColor: finalStyle?.iconColor || iconColor,
              }}
              record={item}
              mode="leftSection"
            />
          ) : null
        }
        right={() =>
          rightSection?.enabled ? (
            <LeftSection
              attributes={{
                ...(rightSection || {}),
                iconColor: finalStyle?.iconColor || iconColor,
                defaultValue: get(bindingValue, 'rightSection.input', false),
              }}
              record={item}
              mode="rightSection"
              handlePress={() =>
                handlePress('rightSection', {
                  record: item,
                  indexRecord: index,
                })
              }
            />
          ) : null
        }
        style={listItemStyle as any}
        titleStyle={titleTextStyle}
        descriptionStyle={subtitleTextStyle}
      />
    );
  };

  const keyExtractor = (item: any, index: number) => index;

  const list: any = (
    <FlatList
      {...isFixedHeight}
      nestedScrollEnabled
      showsHorizontalScrollIndicator={false}
      data={listData}
      keyExtractor={keyExtractor}
      renderItem={renderItemList}
      {...(isShowFooter && {
        ListFooterComponent: ListFooterComponent,
      })}
    />
  );

  // Apply finalStyle to main container
  const containerStyle = {
    ...styles,
    opacity: finalStyle?.opacity || opacity || 1,
    ...(listData && listData.length > 0
      ? {
          backgroundColor: finalStyle?.backgroundColor || backgroundColor,
        }
      : {}),
    borderColor: finalStyle?.borderColor || borderColor,
    borderWidth: finalStyle?.borderWidth || borderWidth,
    borderRadius: finalStyle?.borderRadius || borderRadius,
    borderStyle:
      finalStyle?.borderStyle !== 'none' && finalStyle?.borderStyle
        ? finalStyle?.borderStyle
        : undefined,
    ...(isWeb && {
      marginTop: 0,
      marginBottom: 0,
      height: attrs.height - 20,
      overflowX: 'hidden',
      overflowY: listData.length > 0 ? 'visible' : 'hidden',
    }),
  };

  return (
    <>
      <List.Section style={containerStyle}>
        {listHeader.enabled && (
          <List.Subheader onPressIn={() => {}} onPressOut={() => {}}>
            Simple List
          </List.Subheader>
        )}
        {listData &&
          listData.length > 0 &&
          (scrollActive && (Platform.OS !== 'web' || !isNil(target)) ? (
            <View style={{ height: attrs.height, margin: 0 }}>{list}</View>
          ) : (
            list
          ))}
      </List.Section>
    </>
  );
};

export default SimpleList;
